import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { buildFiltersConfigResolved, parseSelectionsFromSearchParams } from "../discoveryFiltersUtils";
import useDiscoveryFilterTemplates from "../useDiscoveryFilterTemplates";
import { AccessibilityClickableDiv } from "@skillz/accessibility";
import Icon from "../../../ui/icons/Icon";
import iconsList from "../../../ui/icons/iconsList";
import DiscoveryFilterDropDown from "./DiscoveryFilterDropDown";
import DiscoveryMobileFilterSheet from "./DiscoveryMobileFilterSheet";
import classes from "./DiscoveryFilterBar.module.css";

const DiscoveryFilterBar = ({ courses = [], onSelectFilter, onApplyFilters, onClear, onFreeSearch }) => {
    
    const [searchParams] = useSearchParams();
    const { texts, lang } = useSelector((s) => s.translate);
    const providers = useSelector((s) => s.uiData.providers);
    const domains = useSelector((s) => s.uiData.domains);
    const tagsMeta = useSelector((s) => s.uiData.tags);
    const filterTemplates = useDiscoveryFilterTemplates();

    const selections = useMemo(
        () => parseSelectionsFromSearchParams(searchParams, filterTemplates, { language: lang }),
        [searchParams, filterTemplates, lang]
    );

    const filtersConfig = useMemo(
        () => buildFiltersConfigResolved(filterTemplates, courses, selections, { providers, domains, tags: tagsMeta }),
        [filterTemplates, courses, selections, providers, domains, tagsMeta]
    );

    const isDefaultFilterValue = (filterName, value) => filterName === "language" && value === lang;
    
    const activeFiltersCount = useMemo(
        () => filterTemplates.reduce((count, filter) => {
            const value = searchParams.get(filter.name);
            return value !== null && !isDefaultFilterValue(filter.name, value) ? count + 1 : count;
        }, 0),
        [filterTemplates, searchParams, lang]
    );

    return (
        <div className={classes.FilterBar}>
            <DiscoveryMobileFilterSheet filtersConfig={filtersConfig} activeFiltersCount={activeFiltersCount} selections={selections} lang={lang} texts={texts} onApplyFilters={onApplyFilters} onFreeSearch={onFreeSearch} onClear={onClear} />
            <div className={classes.FreeSearchWrap}>
                <span className={classes.FreeSearchIconWrap} aria-hidden="true">
                    <Icon name={iconsList.discoverySearch} size="24px" color="#925FE2" />
                </span>
                <input type="search" placeholder={texts.discoveryFreeSearchPlaceholder} value={selections.free ?? ""} onChange={(e) => onFreeSearch(e.target.value)} aria-label={texts.discoveryFreeSearchPlaceholder} className={classes.FreeSearchInput} />
            </div>
            <div className={classes.DropDownsRow}>
                {filtersConfig.map((filter) => (
                    <DiscoveryFilterDropDown key={filter.name} filter={filter} selectedId={selections[filter.name]} lang={lang} onSelectOption={(option) => onSelectFilter(filter.name, option)} />
                ))}
                <AccessibilityClickableDiv className={classes.ClearSearch} onClick={onClear} role="button">
                    <Icon name={iconsList.clearSearch} size="16px" color="#000" className={classes.ClearSearchIcon} />
                    <span className={classes.ClearSearchText}>{texts.discoveryClearSearch}</span>
                </AccessibilityClickableDiv>
            </div>
        </div>
    );
};

export default DiscoveryFilterBar;
